import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import { buildUserInfoFromIdToken } from "../../lib/contexts/buildUserInfoFromIdToken";
import { useCallback, useEffect } from "react";
import { Auth as AmplifyAuth, Hub } from "aws-amplify";
import { isUserAuthorized } from "@common/domain/auth";
import { resetAlertState } from "../../lib/api/Auth";
import { Outlet, useNavigate } from "react-router-dom";
import { Authenticator, useAuthenticator, View, Button, Divider } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";

export function Auth({ showGoogleSignIn = true, isAuthorized }: { showGoogleSignIn?: boolean; isAuthorized?: (groups?: string[]) => boolean }) {
  const navigate = useNavigate();
  const { userInfo, updateUserInfo } = useUserInfoContext();

  // currentSession() transparently refreshes an expired idToken via the refresh token,
  // so this resolves the returning-user case that the one-shot localStorage read in
  // UserInfoProvider misses (stored idToken already expired -> page rendered blank).
  const hydrateFromSession = useCallback(async () => {
    try {
      const session = await AmplifyAuth.currentSession();
      const idToken = session.getIdToken();
      const payload: any = idToken.payload;
      updateUserInfo(buildUserInfoFromIdToken(payload, idToken.getJwtToken()));
    } catch {
      // Not authenticated; the Authenticator below renders the sign-in form.
    }
  }, [updateUserInfo]);

  useEffect(() => {
    if (!userInfo) {
      hydrateFromSession();
    }
    const intervalId = setInterval(() => {
      hydrateFromSession();
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [userInfo, hydrateFromSession]);

  const checkAuthorized = isAuthorized ?? isUserAuthorized;

  useEffect(() => {
    if (userInfo) {
      if (!checkAuthorized(userInfo.groups)) {
        navigate("/unauthorized");
      }
    }
  }, [userInfo, navigate, checkAuthorized]);

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload: { event, data } }) => {
      switch (event) {
        case "signIn":
        case "cognitoHostedUI":
          resetAlertState();
          const groups = data.signInUserSession.idToken.payload["cognito:groups"] || [];
          updateUserInfo(buildUserInfoFromIdToken(
            data.signInUserSession.idToken.payload,
            data.signInUserSession.idToken.jwtToken,
          ));
          if (!checkAuthorized(groups)) {
            navigate("/unauthorized");
          }
          break;
        case "tokenRefresh":
          // Silent background refresh emits this (not signIn); re-hydrate so a
          // long-open session that crosses idToken expiry keeps rendering.
          hydrateFromSession();
          break;
        case "signIn_failure":
        case "cognitoHostedUI_failure":
          console.log("Error", data);
          break;
      }
    });
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const components = {
    SignIn: {
      /**
       * Custom Footer to show how to manually add social login buttons.
       * This gives more control over the UI than just using the socialProviders prop.
       */
      Footer() {
        const { toFederatedSignIn, toResetPassword } = useAuthenticator();

        return (
          <View textAlign="center" padding="0 2rem 2rem 2rem">
            <Button onClick={toResetPassword} variation="link" width="100%">
              Forgot Password?
            </Button>
            {showGoogleSignIn && <>
              <Divider size="small" label="OR" margin="1rem 0" />
              <Button
                onClick={() => toFederatedSignIn({ provider: "Google" })}
                width="100%"
                style={{
                  backgroundColor: "white",
                  color: "rgba(0, 0, 0, 0.54)",
                  borderColor: "#ddd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  fontWeight: "bold",
                  boxShadow: "0 2px 4px 0 rgba(0,0,0,0.25)",
                }}
              >
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
                  fill="#4285F4"
                />
                <path
                  d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
                  fill="#34A853"
                />
                <path
                  d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
                  fill="#FBBC05"
                />
                <path
                  d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.443 2.041.957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"
                  fill="#EA4335"
                />
              </svg>
              Sign in with Google
              </Button>
            </>}
          </View>
        );
      },
    },
  };

  return (
    <Authenticator hideSignUp components={components}>
      {userInfo && checkAuthorized(userInfo.groups) ? <Outlet /> : null}
    </Authenticator>
  );
}
